import { Component, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { SocketService } from 'src/app/services/socket.service';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { MatPaginator, MatTableDataSource, MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatSnackBar } from '@angular/material';
import { AlarmaDetalleDialogComponent } from './alarma-detalle-dialog/alarma-detalle-dialog.component';
import { ClienteinfoService } from 'src/app/services/clienteinfo.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { ConfirmacionComponent } from 'src/app/home/confirmacion/confirmacion.component';
import { tryCatch } from 'rxjs/internal-compatibility';
import { AlarmasPermisosService } from 'src/app/services/alarmas-permisos.service';

// Indice del tab "Historial" en el mat-tab-group. Si cambia el orden de los
// tabs en el HTML hay que actualizarlo aca.
const TAB_HISTORIAL_INDEX = 1;
// TTL del cache del historial. Mientras este vigente, alternar tabs no
// re-dispara la query pesada. Acciones del propio agente (resolver, reenviar)
// igual lo invalidan via force=true.
const HISTORIAL_CACHE_TTL_MS = 30_000;

@Component({
  selector: 'app-alerta-flotante',
  templateUrl: './alerta-flotante.component.html',
  styleUrls: ['./alerta-flotante.component.scss']
})

export class AlertaFlotanteComponent implements OnInit {

  reintentos = 5;
  alarmas = [];
  histalarmas = [];
  parpadea = false;
  count = 0;
  panelAbierto = false;
  agente_id = null;

  animarNumero = false;
  audio = new Audio('assets/audio/alerta.wav');
  selectedTab = 0;

  // Timestamp del ultimo fetch exitoso del historial. Si alternar tabs ocurre
  // antes de que pasen HISTORIAL_CACHE_TTL_MS, no se re-pega al backend.
  private histalarmasLoadedAt: number = 0;

  // Set de idescenario que el area del agente puede ver. Se carga 1 vez al
  // montar el componente desde el endpoint escenariosArea. Si llega un evento
  // socket alarma-pendiente con escenario fuera del set, se descarta sin
  // disparar getPendientes (filtro temprano).
  private escenariosVisibles: Set<number> = new Set();
  // Flag explicito: true solo si la carga fue exitosa. Mientras sea false el
  // filtro queda inactivo y refetcheamos todo (fail-safe — mejor refetch de
  // mas que perder una alarma legitima por falla de red).
  private escenariosCargados: boolean = false;

  constructor(
    private gateway: GatewayService,
    // private alarmasService: AlarmasService,
    private localsave: LocalSaveService,
    private socketService: SocketService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private clienteinfo: ClienteinfoService,
    private router: Router,
    private alarmasPermisos: AlarmasPermisosService,
  ) {
    this.agente_id = this.localsave.get('usuarioPGV').idagente;
  }

  ngOnInit() {
    // cargar alarmas pendientes de BD
    this.getPendientes();

    // Cargar la lista de escenarios visibles para esta area. Mientras no
    // termine de cargar (o si falla), el filtro queda inactivo (refetchea
    // todo). Una vez cargado, se filtran los eventos cuyo escenario no
    // pertenezca al area.
    this.cargarEscenariosVisibles();

    // cuando llega una alarma por socket
    this.socketService.onNuevaAlarma((data) => {
      // Filtro temprano: si esta alarma no corresponde a la area del agente
      // (segun escenariosVisibles), no hacemos refetch. Esto evita el
      // "thundering herd" donde todos los agentes refetchean por una alarma
      // que solo aplica a un subset.
      // Fail-safe: si la lista aun no se cargo o fallo, NO filtramos.
      if (this.escenariosCargados) {
        const escenario = this.extraerEscenario(data);
        if (escenario && !this.escenariosVisibles.has(escenario)) {
          return; // no me corresponde, skip
        }
      }
      this.getPendientes();
    });

    // cuando otro agente toma una alarma
    this.socketService.onAlarmaTomada((data) => {
      this.alarmas = this.alarmas.filter(a => Number(a.id) !== Number(data.id));
      this.updateState();
    });
  }

  /**
   * Extrae el campo `escenario` del payload del socket. El payload llega en
   * dos formas posibles:
   *   - { from, data: { ..., escenario } }  (forma "envelope" del tecnico)
   *   - { ..., escenario }                  (forma plana, ej. system_*)
   * Devuelve null si no hay escenario interpretable.
   */
  private extraerEscenario(payload: any): number | null {
    if (!payload) { return null; }
    const inner = payload && payload.data ? payload.data : payload;
    const raw = inner && inner.escenario;
    if (raw === null || raw === undefined || raw === '') { return null; }
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }

  /**
   * Trae la lista de idescenario visibles para la area del agente y la
   * cachea en memoria. Solo se llama 1 vez al montar el componente. Si
   * falla, `escenariosCargados` queda false y el filtro no aplica
   * (comportamiento equivalente al actual: refetch a todo).
   */
  private cargarEscenariosVisibles(): void {
    const usuario = this.localsave.get('usuarioPGV');
    if (!usuario || !usuario.idareaagente) { return; }
    this.gateway.get('getEscenariosArea', { idareaagente: usuario.idareaagente })
      .then((res: any) => {
        const rows = res && Array.isArray(res.rows) ? res.rows : [];
        this.escenariosVisibles = new Set(rows.map((r: any) => Number(r.idescenario)));
        this.escenariosCargados = true;
      })
      .catch(() => {
        // Mantener escenariosCargados=false -> el filtro queda inactivo
        // y los eventos siguen refetcheando como antes.
      });
  }

  /**
   * Trae solo el listado de alarmas pendientes del agente. Se usa en cada
   * evento de socket alarma-pendiente (broadcast a TODOS los agentes) y al
   * tomar/resolver alarmas. Es la query "barata" que SI debe refrescar en
   * tiempo real.
   *
   * NO toca el historial — eso vive en getHistorial(), que solo se llama
   * cuando el usuario abre el tab Historial (lazy load + cache 30s).
   */
  getPendientes() {
    const usuario = this.localsave.get('usuarioPGV');
    return this.gateway.get('getPendientes', {
      agente_id: usuario.idagente,
      idarea: usuario.IDAREA,
      idareaagente: usuario.idareaagente,
    }).then((result: any) => {
      this.alarmas = Array.isArray(result) ? result : [];
      if (this.alarmas.length > this.count) {
        // Hay mas alarmas que antes -> sonido
        this.audio.volume = 0.7;
        this.audio.currentTime = 0;
        this.audio.play().catch(_ => { /* silencioso */ });
      }
      this.updateState();
      return true;
    });
  }

  /**
   * Trae el historial de alarmas resueltas del propio agente. Es la query
   * pesada (multiples JOINs). Se invoca SOLO cuando:
   *   - El usuario hace click en el tab Historial.
   *   - El propio agente ejecuta una accion que modifica el historial
   *     (reenviar accion), llamando con force=true para saltearse el cache.
   *
   * Cache de HISTORIAL_CACHE_TTL_MS: si el ultimo fetch fue hace menos de eso
   * y no se fuerza, no se pega al backend (resuelve inmediato con la data en
   * memoria). Evita spam si el usuario alterna tabs.
   */
  getHistorial(force: boolean = false): Promise<boolean> {
    const ahora = Date.now();
    if (!force && this.histalarmasLoadedAt && (ahora - this.histalarmasLoadedAt) < HISTORIAL_CACHE_TTL_MS) {
      return Promise.resolve(false); // sirvio el cache
    }
    const usuario = this.localsave.get('usuarioPGV');
    return this.gateway.get('getHistAlarmasxAgente', {
      agente_id: usuario.idagente,
      idarea: usuario.IDAREA,
      idareaagente: usuario.idareaagente,
    }).then((result: any) => {
      this.histalarmas = Array.isArray(result) ? result : [];
      this.histalarmasLoadedAt = Date.now();
      return true;
    });
  }

  /**
   * Handler del cambio de tab. Solo dispara la carga del historial cuando
   * el usuario navega al tab Historial. El cache evita refetch innecesario.
   */
  onTabChange(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    if (event.index === TAB_HISTORIAL_INDEX) {
      this.getHistorial();
    }
  }

  updateState() {
    this.count = this.alarmas.length;
    // activar animaciones
    this.parpadea = this.count > 0;
    this.animarNumero = this.count > 0;
  }

  abrirPanel() {
    // if (this.count == 0) return;
    this.panelAbierto = !this.panelAbierto;
    this.selectedTab = 0;
    this.getPendientes();
  }

  tomarAlarma(alarma) {
    // RF-22: informative alarms (tipo=3) — auto-resolve, no dialog
    if (Number(alarma.tipo_escenario) === 3) {
      this.gateway.get('tomarAlarma', { id: alarma.id, agente_id: this.localsave.get('usuarioPGV').idagente })
        .then(async (result: any) => {
          if (Number(result.response) === 1) {
            this.socketService.tomarAlarma({ id: alarma.id });
            this.snackbar.open('Alarma informativa cerrada', 'Cerrar', { duration: 3000 });
            this.getPendientes();
          } else {
            this.snackbar.open(result.msgerror || 'No se pudo tomar la alarma', 'Cerrar', { duration: 7000 });
            this.getPendientes();
          }
        });
      return;
    }

    if (Number(alarma.agente_id) === Number(this.agente_id)) {
      this.verDetalleAlarma(alarma);
    } else {
      this.gateway.get('tomarAlarma', { id: alarma.id, agente_id: this.localsave.get('usuarioPGV').idagente })
        .then(async (result: any) => {
          if (Number(result.response) === 1) {
            // enviar socket a los otros agentes
            this.socketService.tomarAlarma({ id: alarma.id });
            await this.getPendientes();
            const alarmaEncontrada = this.alarmas.find(a => Number(a.id) === Number(alarma.id));
            if (alarmaEncontrada) {
              this.verDetalleAlarma(alarmaEncontrada);
            }
          } else {
            this.getPendientes();
            this.snackbar.open(result.msgerror, 'Cerrar', { duration: 7000 });
          }

        });
    }
  }

  verDetalleAlarma(alarma) {
    /**
     * Validar si es programador de citas, si es programador de citas entonces:
     *  - cerrar la llamada del cliente actual y crear una llamada nueva con el nuevo cliente
     *  - refrescar los datos del nuevo cliente.
     *  - gestionar y al crear la cita, 
     */

    /*
    if (this.localsave.get('usuarioPGV').idareaagente == 2 || this.localsave.get('usuarioPGV').idareaagente == 7) {
      this.gateway.get('gestionarclienteahora', alarma)
        .then((result: any) => {
          if (result.response == 2) {
            this.gateway.get('cambio_estado', { IDAGENTE: this.localsave.get('usuarioPGV').idagente, IDESTADO: 1, IDCAMPANA: result.result.idcampana })
              .then((result: any) => {
                this.clienteinfo.setState(1);

                const current = this.router.url;
                if (current.includes('homeagente/atencioncliente')) {
                  this.router.navigate(['homeagente/atencionclientes']);
                } else {
                  this.router.navigate(['homeagente/atencioncliente']);
                }
                // this.spinner.hide();
              });
          } else {
            this.snackbar.open(`No fue posible completar la acción en este momento. Por favor, inténtalo nuevamente más tarde.`, 'Cerrar', { duration: 8000 });
            // this.spinner.hide();
          }
        });
    } else {
      const dialogRef = this.dialog.open(AlarmaDetalleDialogComponent, {
        width: '500px',
        data: alarma,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (!result) return;
        alarma['accion'] = result.accion;
        alarma['comentario_agente'] = result.comentario;
        // tipo=4 "Cambio de equipo": el agente escribio la orden de instalacion.
        // El backend la usa para clonar la cita de reparacion hacia instalacion.
        if (result.orden_instalacion) {
          alarma['orden_instalacion'] = result.orden_instalacion;
        }
        this.gateway.get('resolverAlarma', alarma)
          .then((resp: any) => {
            this.socketService.enviarRespuesta(`tecnico_${alarma.tecnico_id}`, alarma);
            if (resp && resp.cita_creada === false && resp.message) {
              this.snackbar.open(resp.message, 'Cerrar', { duration: 6000 });
            } else if (resp && resp.cita_creada === true) {
              this.snackbar.open('Cambio de equipo aprobado. Cita de instalación creada (#' + resp.id_cita_instalacion + ').', 'Cerrar', { duration: 5000 });
            }
            this.getPendientes();
          });
      });
    }
    */

    const dialogRef = this.dialog.open(AlarmaDetalleDialogComponent, {
      width: '500px',
      data: { ...alarma, show_options: true, puede_gestionar: this.alarmasPermisos.puedeGestionar() },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      if (Number(result.accion) === 4) {
        // TRANSFERIR: llama al backend que actualiza la alarma + inserta log.
        const user = this.localsave.get('usuarioPGV') || {};
        this.gateway.get('transferirAlarma', {
          id_alarma: alarma.id,
          idareaagente_destino: result.idareaagente_destino,
          idtipologia_agente: result.idtipologia_agente,
          idsubtipologia_agente: result.idsubtipologia_agente,
          idrazon_agente: result.idrazon_agente,
          comentario: result.comentario,
          idusuario: user.IDU || user.idagente,
          nombre_usuario: this.localsave.get('userLoggedIn') || null,
          correo_usuario: this.localsave.get('email') || null,
          idareaagente_origen: user.idareaagente || null
        }).then((r: any) => {
          if (Number(r.response) === 1) {
            this.snackbar.open(r.message || 'Alarma transferida.', 'Cerrar', { duration: 3000 });
          } else {
            this.snackbar.open(r.message || 'No se pudo transferir.', 'Cerrar', { duration: 4000 });
          }
          this.getPendientes();
        }).catch(() => {
          this.snackbar.open('Error al transferir.', 'Cerrar', { duration: 3000 });
        });
        return;
      }
      if (Number(result.accion) === 3) {
        this.gateway.get('gestionarclienteahora', alarma)
          .then((result: any) => {
            if (Number(result.response) === 1) {
              this.gateway.get('cambio_estado', { IDAGENTE: this.localsave.get('usuarioPGV').idagente, IDESTADO: 1, IDCAMPANA: result.result.idcampana })
                .then((result: any) => {
                  this.clienteinfo.setState(1);

                  const current = this.router.url;
                  if (current.includes('homeagente/atencioncliente')) {
                    this.router.navigate(['homeagente/atencionclientes']);
                  } else {
                    this.router.navigate(['homeagente/atencioncliente']);
                  }
                  // this.spinner.hide();
                });
            } else {
              this.snackbar.open(result.msgerror, 'Cerrar', { duration: 8000 });
              // this.spinner.hide();
            }
          });
      } else {
        alarma['accion'] = result.accion;
        alarma['comentario_agente'] = result.comentario;
        // Re-tipificacion enviada por el agente (nuevos campos de escalamiento)
        alarma['idtipologia_agente'] = result.idtipologia_agente || null;
        alarma['idsubtipologia_agente'] = result.idsubtipologia_agente || null;
        alarma['idrazon_agente'] = result.idrazon_agente || null;
        // "Liberar y reintentar" (No Localizado/Ausente en banda AM): el backend
        // valida subtipologia disparadora + banda AM antes de liberar la cita.
        alarma['liberar_reintento'] = result.liberar_reintento || false;
        // tipo=4 "Cambio de equipo": el agente escribio la orden de instalacion.
        // El backend la usa para clonar la cita de reparacion hacia instalacion.
        if (result.orden_instalacion) {
          alarma['orden_instalacion'] = result.orden_instalacion;
        }
        this.gateway.get('resolverAlarma', alarma)
          .then((resp: any) => {
            this.socketService.enviarRespuesta(`tecnico_${alarma.tecnico_id}`, alarma);
            if (resp && resp.cita_creada === false && resp.message) {
              this.snackbar.open(resp.message, 'Cerrar', { duration: 6000 });
            } else if (resp && resp.cita_creada === true) {
              this.snackbar.open('Cambio de equipo aprobado. Cita de instalación creada (#' + resp.id_cita_instalacion + ').', 'Cerrar', { duration: 5000 });
            }
            this.getPendientes();
          });
      }
    });
  }

  vermasInfoAlarma(alarma) {
    const dialogRef = this.dialog.open(AlarmaDetalleDialogComponent, {
      width: '500px',
      data: { ...alarma, show_options: false },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
    });
  }

  /**
   * Devuelve "lat, lng" si ambos vienen, sino null.
   */
  ubicacionTecnico(alarma): string | null {
    const lat = alarma && alarma.tecnico_latitud;
    const lng = alarma && alarma.tecnico_longitud;
    if (lat === null || lat === undefined || lat === '' ||
        lng === null || lng === undefined || lng === '') {
      return null;
    }
    return `${lat}, ${lng}`;
  }

  /**
   * Copia "lat, lng" al portapapeles. Usa la API moderna y un fallback con
   * textarea para navegadores viejos.
   */
  copiarUbicacion(alarma): void {
    const texto = this.ubicacionTecnico(alarma);
    if (!texto) {
      this.snackbar.open('El tecnico no tiene ubicacion registrada', 'Cerrar', { duration: 2500 });
      return;
    }
    const okMsg = 'Ubicacion copiada: ' + texto;
    if (navigator && (navigator as any).clipboard && (navigator as any).clipboard.writeText) {
      (navigator as any).clipboard.writeText(texto)
        .then(() => this.snackbar.open(okMsg, 'Cerrar', { duration: 2500 }))
        .catch(() => this.copiarConFallback(texto, okMsg));
    } else {
      this.copiarConFallback(texto, okMsg);
    }
  }

  private copiarConFallback(texto: string, okMsg: string) {
    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.snackbar.open(okMsg, 'Cerrar', { duration: 2500 });
    } catch (e) {
      this.snackbar.open('No se pudo copiar la ubicacion', 'Cerrar', { duration: 3000 });
    }
  }

  reenviarAlarma(alarma) {
    try {
      const jsonrespuesta = alarma.jsonrespuesta ? JSON.parse(alarma.jsonrespuesta) : null;
      if (jsonrespuesta && alarma.reintento < this.reintentos) {
        alarma.reintento++;
        const dialogRef = this.dialog.open(ConfirmacionComponent, {
          disableClose: true,
          width: '350px',
          data: { sms: 'reenviar la respuesta al técnico', addcomment: false }
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result.resp) {
            this.gateway.get('reintentoResolverAlarma', alarma)
              .then((result: any) => {
                this.socketService.enviarRespuesta(`tecnico_${alarma.tecnico_id}`, jsonrespuesta);
                this.getPendientes();
                // El reintento se ejecuta desde el tab Historial -> refrescar
                // historial forzado para reflejar el contador de reintentos
                // actualizado (afecta visibilidad del boton "Reenviar accion").
                this.getHistorial(true);
              });
          }
        });
      } else {
        if (alarma.reintento >= this.reintentos) {
          this.snackbar.open(`Has superado el número de ${this.reintentos} reintento(s) valido(s).`, 'Cerrar', { duration: 3000 });
        } else {
          this.snackbar.open('No se pudo reenviar la alarma. Intentelo más tarde.', 'Cerrar', { duration: 3000 });
        }
      }
    } catch (error) {
      this.snackbar.open('No se pudo reenviar la alarma. Intentelo más tarde.', 'Cerrar', { duration: 3000 });
    }
  }

  // const dialogRef = this.dialog.open(ConfirmacionComponent, {
  //       disableClose: true,
  //       width: '350px',
  //       data: { sms: 'reenviar la respuesta al técnico', addcomment: false }
  //     });

  //     dialogRef.afterClosed().subscribe((result) => {
  //       if (result.resp) {

  //       }
  //     });
}
